import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Building2, Phone, Mail, Globe, Users, TrendingUp, Plus, Loader2, Edit2, ShoppingCart, Truck, Save, X, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const INDUSTRIES = ['Tecnologia Agricola','Agricoltura','Macchinari Agricoli','Distribuzione','Consulenza','Alimentare','Energia','Altro'];
const SIZES = ['1-10','11-50','51-200','201-500','500+'];
const SOURCES = ['Web','Referral','Fiera','Cold Call','Social','Altro'];

const INTERACTION_TYPES = [
  {value:'call_out', label:'Chiamata Uscente'},
  {value:'call_in', label:'Chiamata Entrante'},
  {value:'email_sent', label:'Email Inviata'},
  {value:'email_received', label:'Email Ricevuta'},
  {value:'meeting', label:'Riunione'},
  {value:'note', label:'Nota'},
];

const DDT_FIELDS = [
  { key: 'codice_cliente',          label: 'Codice Cliente' },
  { key: 'ddt_destinazione_nome',   label: 'Luogo di Destinazione' },
  { key: 'ddt_destinazione_indirizzo', label: 'Indirizzo' },
  { key: 'ddt_destinazione_cap_citta', label: 'CAP / Città' },
  { key: 'ddt_causale_trasporto',   label: 'Causale Trasporto' },
  { key: 'ddt_aspetto_beni',        label: 'Aspetto Esteriore Beni' },
  { key: 'ddt_trasporto_a_cura_di', label: 'Trasporto a cura di' },
  { key: 'ddt_vettore',             label: 'Vettore' },
];

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInteraction, setShowInteraction] = useState(false);
  const [intForm, setIntForm] = useState({ type: 'note', subject: '', content: '' });
  const [saving, setSaving] = useState(false);
  const [editingDDT, setEditingDDT] = useState(false);
  const [ddtForm, setDdtForm] = useState({});
  const [savingDDT, setSavingDDT] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [editCompanyForm, setEditCompanyForm] = useState({});
  const [savingCompany, setSavingCompany] = useState(false);

  const fetchCompany = async () => {
    try {
      const res = await axios.get(`${API}/companies/${id}`);
      setCompany(res.data);
    } catch (e) {
      toast.error('Azienda non trovata');
      navigate('/companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompany(); }, [id]);

  const startEditDDT = () => {
    const initial = {};
    DDT_FIELDS.forEach(f => { initial[f.key] = company[f.key] || ''; });
    setDdtForm(initial);
    setEditingDDT(true);
  };

  const startEditCompany = () => {
    setEditCompanyForm({
      name: company.name || '',
      industry: company.industry || '',
      size: company.size || '',
      email: company.email || '',
      phone: company.phone || '',
      website: company.website || '',
      lead_source: company.lead_source || '',
      notes: company.notes || '',
      tags: (company.tags || []).join(', '),
      // Identificazione
      ragione_sociale: company.ragione_sociale || '',
      nome_commerciale: company.nome_commerciale || '',
      partita_iva: company.partita_iva || '',
      codice_fiscale: company.codice_fiscale || '',
      codice_cliente: company.codice_cliente || '',
      // Sede
      indirizzo_sede_legale: company.indirizzo_sede_legale || '',
      cap: company.cap || '',
      citta: company.citta || '',
      provincia: company.provincia || '',
      stato: company.stato || '',
      indirizzo_sede_operativa: company.indirizzo_sede_operativa || '',
      // Contatti
      pec: company.pec || '',
      // DDT
      ddt_destinazione_nome: company.ddt_destinazione_nome || '',
      ddt_destinazione_indirizzo: company.ddt_destinazione_indirizzo || '',
      ddt_destinazione_cap_citta: company.ddt_destinazione_cap_citta || '',
      ddt_causale_trasporto: company.ddt_causale_trasporto || '',
      ddt_aspetto_beni: company.ddt_aspetto_beni || '',
      ddt_trasporto_a_cura_di: company.ddt_trasporto_a_cura_di || '',
      ddt_vettore: company.ddt_vettore || '',
      // Categoria cliente (per DDT type)
      customer_category: company.customer_category || '',
    });
    setShowEditCompany(true);
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    setSavingCompany(true);
    try {
      const tags = editCompanyForm.tags ? editCompanyForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      await axios.put(`${API}/companies/${id}`, { ...editCompanyForm, tags });
      toast.success('Azienda aggiornata');
      setShowEditCompany(false);
      fetchCompany();
    } catch { toast.error('Errore salvataggio'); }
    finally { setSavingCompany(false); }
  };

  const setECF = (k, v) => setEditCompanyForm(f => ({ ...f, [k]: v }));

  const saveDDT = async () => {
    setSavingDDT(true);
    try {
      await axios.put(`${API}/companies/${id}`, ddtForm);
      toast.success('Dati aggiornati');
      setEditingDDT(false);
      fetchCompany();
    } catch { toast.error('Errore salvataggio'); }
    finally { setSavingDDT(false); }
  };

  const addInteraction = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/interactions`, { ...intForm, company_id: id });
      toast.success('Interazione registrata');
      setShowInteraction(false);
      setIntForm({ type: 'note', subject: '', content: '' });
      fetchCompany();
    } catch { toast.error('Errore'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!company) return null;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/companies')} data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold" style={{fontFamily:'Manrope, sans-serif'}}>{company.name}</h1>
          <p className="text-muted-foreground text-sm">{company.industry} {company.size ? `• ${company.size} dipendenti` : ''}</p>
        </div>
        {canEdit('companies') && (
          <div className="flex items-center gap-1 mr-1" title="Tipo DDT che verrà generato">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Cat.</span>
            <select
              value={company.customer_category || ''}
              onChange={async (e) => {
                const val = e.target.value;
                try {
                  await axios.put(`${API}/companies/${id}`, { customer_category: val || null });
                  setCompany({ ...company, customer_category: val });
                  toast.success(val === 'distributore' ? 'Categoria: Distributore (DDT con prezzi)' : val === 'gdo' ? 'Categoria: GDO (DDT senza prezzi)' : 'Categoria rimossa (default GDO)');
                } catch { toast.error('Errore aggiornamento categoria'); }
              }}
              className={`h-8 text-xs rounded-md px-2 border font-medium ${
                company.customer_category === 'distributore'
                  ? 'border-purple-300 bg-purple-50 text-purple-900 dark:bg-purple-900/30 dark:text-purple-100'
                  : company.customer_category === 'gdo'
                    ? 'border-blue-300 bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100'
                    : 'border-input bg-background text-muted-foreground'
              }`}
              data-testid="company-category-toggle"
            >
              <option value="">— non classificato —</option>
              <option value="gdo">GDO</option>
              <option value="distributore">Distributore</option>
            </select>
          </div>
        )}
        {canEdit('companies') && (
          <Button variant="outline" size="sm" onClick={startEditCompany} data-testid="edit-company-btn">
            <Pencil size={14} className="mr-2" />Modifica
          </Button>
        )}
        {canCreate('orders') && (
          <Button variant="outline" size="sm" onClick={() => navigate(`/orders?company_id=${id}&new=1`)} data-testid="create-order-from-company-btn">
            <ShoppingCart size={14} className="mr-2" />Nuovo Ordine
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="space-y-4">
          {/* Informazioni base */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Informazioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {company.codice_cliente && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Codice Cliente</span>
                  <span className="font-mono font-semibold text-primary">{company.codice_cliente}</span>
                </div>
              )}
              {company.ragione_sociale && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Ragione Sociale</span>
                  <span className="font-medium text-right">{company.ragione_sociale}</span>
                </div>
              )}
              {company.nome_commerciale && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Brand</span>
                  <span className="text-right">{company.nome_commerciale}</span>
                </div>
              )}
              {company.partita_iva && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">P. IVA</span>
                  <span className="font-mono text-right">{company.partita_iva}</span>
                </div>
              )}
              {company.codice_fiscale && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Cod. Fiscale</span>
                  <span className="font-mono text-right">{company.codice_fiscale}</span>
                </div>
              )}
              {(company.indirizzo_sede_legale || company.citta) && (
                <div className="text-sm">
                  <p className="text-muted-foreground text-xs mb-0.5">Sede</p>
                  {company.indirizzo_sede_legale && <p className="text-xs">{company.indirizzo_sede_legale}</p>}
                  {(company.cap || company.citta || company.provincia) && (
                    <p className="text-xs">{[company.cap, company.citta, company.provincia].filter(Boolean).join(' ')}</p>
                  )}
                  {company.stato && <p className="text-xs text-muted-foreground">{company.stato}</p>}
                </div>
              )}
              {company.email && <div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-muted-foreground" /><a href={`mailto:${company.email}`} className="text-primary hover:underline">{company.email}</a></div>}
              {company.pec && <div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-muted-foreground" /><span className="text-xs text-muted-foreground">PEC: </span><a href={`mailto:${company.pec}`} className="text-xs hover:underline truncate">{company.pec}</a></div>}
              {company.phone && <div className="flex items-center gap-2 text-sm"><Phone size={14} className="text-muted-foreground" /><span>{company.phone}</span></div>}
              {company.website && <div className="flex items-center gap-2 text-sm"><Globe size={14} className="text-muted-foreground" /><a href={company.website} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate">{company.website}</a></div>}
              {company.lead_source && (
                <div><span className="text-xs text-muted-foreground">Fonte: </span><Badge variant="outline" className="text-xs">{company.lead_source}</Badge></div>
              )}
              {company.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {company.tags.map(t => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
                </div>
              )}
              {company.notes && <p className="text-sm text-muted-foreground">{company.notes}</p>}
            </CardContent>
          </Card>

          {/* Stats */}
          <Card>
            <CardContent className="p-4 grid grid-cols-2 gap-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary" style={{fontFamily:'Manrope, sans-serif'}}>{company.contacts?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Contatti</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary" style={{fontFamily:'Manrope, sans-serif'}}>{company.opportunities?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Opportunità</p>
              </div>
            </CardContent>
          </Card>

          {/* Anagrafica DDT */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <Truck size={14} />Dati Trasporto DDT
                </CardTitle>
                {!editingDDT && canEdit('companies') && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={startEditDDT} data-testid="edit-ddt-btn">
                    <Edit2 size={13} />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingDDT ? (
                <div className="space-y-3">
                  {DDT_FIELDS.map(({ key, label }) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{label}</Label>
                      <Input
                        value={ddtForm[key] || ''}
                        onChange={e => setDdtForm(f => ({ ...f, [key]: e.target.value }))}
                        className="h-8 text-sm"
                        data-testid={`ddt-field-${key}`}
                      />
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditingDDT(false)}>
                      <X size={12} className="mr-1.5" />Annulla
                    </Button>
                    <Button size="sm" className="flex-1" onClick={saveDDT} disabled={savingDDT} data-testid="save-ddt-btn">
                      {savingDDT ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Save size={12} className="mr-1.5" />}Salva
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {DDT_FIELDS.map(({ key, label }) => {
                    const val = company[key];
                    if (!val) return null;
                    return (
                      <div key={key} className="flex justify-between items-start gap-2 text-sm">
                        <span className="text-muted-foreground text-xs whitespace-nowrap">{label}</span>
                        <span className="text-right text-xs font-medium">{val}</span>
                      </div>
                    );
                  })}
                  {!DDT_FIELDS.some(f => company[f.key]) && (
                    <p className="text-xs text-muted-foreground">Nessun dato inserito. Clicca modifica per aggiungere.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contacts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Contatti</CardTitle>
            </CardHeader>
            <CardContent>
              {company.contacts?.length > 0 ? (
                <div className="space-y-2">
                  {company.contacts.map(ct => (
                    <div key={ct._id || ct.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => navigate(`/contacts/${ct._id || ct.id}`)}>
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users size={13} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{ct.first_name} {ct.last_name}</p>
                        <p className="text-xs text-muted-foreground">{ct.position || ct.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Nessun contatto</p>}
            </CardContent>
          </Card>

          {/* Opportunities */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Opportunità</CardTitle>
            </CardHeader>
            <CardContent>
              {company.opportunities?.length > 0 ? (
                <div className="space-y-2">
                  {company.opportunities.map(opp => (
                    <div key={opp._id || opp.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                      <div>
                        <p className="text-sm font-medium">{opp.title}</p>
                        <p className="text-xs text-muted-foreground">€{opp.value?.toLocaleString('it-IT')}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">{opp.stage}</Badge>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Nessuna opportunità</p>}
            </CardContent>
          </Card>

          {/* Interaction timeline */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Storico Interazioni</CardTitle>
                {canCreate('contacts') && (
                  <Button size="sm" variant="outline" onClick={() => setShowInteraction(true)}>
                    <Plus size={13} className="mr-1.5" />Aggiungi
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {company.interactions?.length > 0 ? (
                <div className="space-y-3">
                  {company.interactions.map((int, i) => (
                    <div key={int._id || int.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                        {i < company.interactions.length - 1 && <div className="w-0.5 flex-1 bg-border mt-1" />}
                      </div>
                      <div className="pb-3 flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">{int.subject}</p>
                          <span className="text-xs text-muted-foreground">{new Date(int.date).toLocaleDateString('it-IT')}</span>
                        </div>
                        {int.content && <p className="text-xs text-muted-foreground mt-0.5">{int.content}</p>}
                        <Badge variant="outline" className="text-xs mt-1">{int.type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Nessuna interazione registrata</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Company Dialog */}
      <Dialog open={showEditCompany} onOpenChange={setShowEditCompany}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Manrope, sans-serif'}}>Modifica Azienda</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveCompany} className="space-y-6 mt-2">
            {/* Identificazione Azienda */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-1">Identificazione Azienda</p>
              <div className="space-y-1.5">
                <Label>Nome azienda *</Label>
                <Input value={editCompanyForm.name || ''} onChange={e => setECF('name', e.target.value)} required data-testid="edit-company-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Ragione Sociale</Label>
                  <Input value={editCompanyForm.ragione_sociale || ''} onChange={e => setECF('ragione_sociale', e.target.value)} placeholder="es. ACME S.r.l." data-testid="edit-ragione-sociale" />
                </div>
                <div className="space-y-1.5">
                  <Label>Nome Commerciale / Brand</Label>
                  <Input value={editCompanyForm.nome_commerciale || ''} onChange={e => setECF('nome_commerciale', e.target.value)} placeholder="es. ACME" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Partita IVA</Label>
                  <Input value={editCompanyForm.partita_iva || ''} onChange={e => setECF('partita_iva', e.target.value)} placeholder="IT12345678901" data-testid="edit-partita-iva" />
                </div>
                <div className="space-y-1.5">
                  <Label>Codice Fiscale</Label>
                  <Input value={editCompanyForm.codice_fiscale || ''} onChange={e => setECF('codice_fiscale', e.target.value)} placeholder="12345678901" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Codice Cliente</Label>
                  <Input value={editCompanyForm.codice_cliente || ''} onChange={e => setECF('codice_cliente', e.target.value)} placeholder="es. CLI001" data-testid="edit-codice-cliente" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Settore</Label>
                  <Select value={editCompanyForm.industry || ''} onValueChange={v => setECF('industry', v)}>
                    <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>{INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Dimensione</Label>
                  <Select value={editCompanyForm.size || ''} onValueChange={v => setECF('size', v)}>
                    <SelectTrigger><SelectValue placeholder="Dipendenti..." /></SelectTrigger>
                    <SelectContent>{SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Fonte lead</Label>
                  <Select value={editCompanyForm.lead_source || ''} onValueChange={v => setECF('lead_source', v)}>
                    <SelectTrigger><SelectValue placeholder="Come ci hanno trovato?" /></SelectTrigger>
                    <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Tag (separati da virgola)</Label>
                  <Input value={editCompanyForm.tags || ''} onChange={e => setECF('tags', e.target.value)} placeholder="premium, priorità..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-2">
                    Categoria Cliente
                    <span className="text-[10px] text-muted-foreground font-normal">(determina il tipo di DDT)</span>
                  </Label>
                  <Select value={editCompanyForm.customer_category || 'none'} onValueChange={v => setECF('customer_category', v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="edit-company-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non classificato (default GDO)</SelectItem>
                      <SelectItem value="gdo">GDO — DDT senza prezzi</SelectItem>
                      <SelectItem value="distributore">Distributore — DDT con prezzi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Note</Label>
                <Textarea value={editCompanyForm.notes || ''} onChange={e => setECF('notes', e.target.value)} rows={2} />
              </div>
            </div>

            {/* Sede e Indirizzi */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-1">Sede e Indirizzi</p>
              <div className="space-y-1.5">
                <Label>Indirizzo Sede Legale</Label>
                <Input value={editCompanyForm.indirizzo_sede_legale || ''} onChange={e => setECF('indirizzo_sede_legale', e.target.value)} placeholder="Via..." />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>CAP</Label>
                  <Input value={editCompanyForm.cap || ''} onChange={e => setECF('cap', e.target.value)} placeholder="00100" />
                </div>
                <div className="space-y-1.5">
                  <Label>Città</Label>
                  <Input value={editCompanyForm.citta || ''} onChange={e => setECF('citta', e.target.value)} placeholder="Roma" />
                </div>
                <div className="space-y-1.5">
                  <Label>Provincia</Label>
                  <Input value={editCompanyForm.provincia || ''} onChange={e => setECF('provincia', e.target.value)} placeholder="RM" maxLength={2} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Stato</Label>
                  <Input value={editCompanyForm.stato || ''} onChange={e => setECF('stato', e.target.value)} placeholder="Italia" />
                </div>
                <div className="space-y-1.5">
                  <Label>Indirizzo Sede Operativa</Label>
                  <Input value={editCompanyForm.indirizzo_sede_operativa || ''} onChange={e => setECF('indirizzo_sede_operativa', e.target.value)} placeholder="Se diversa dalla sede legale" />
                </div>
              </div>
            </div>

            {/* Contatti */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-1">Contatti</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Telefono</Label>
                  <Input value={editCompanyForm.phone || ''} onChange={e => setECF('phone', e.target.value)} placeholder="+39 06 1234567" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email generale</Label>
                  <Input type="email" value={editCompanyForm.email || ''} onChange={e => setECF('email', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>PEC</Label>
                  <Input type="email" value={editCompanyForm.pec || ''} onChange={e => setECF('pec', e.target.value)} placeholder="azienda@pec.it" />
                </div>
                <div className="space-y-1.5">
                  <Label>Sito web</Label>
                  <Input value={editCompanyForm.website || ''} onChange={e => setECF('website', e.target.value)} placeholder="https://..." />
                </div>
              </div>
            </div>

            {/* Dati Trasporto DDT */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b pb-1">Dati Trasporto DDT</p>
              <p className="text-xs text-muted-foreground -mt-1">Questi dati verranno pre-compilati automaticamente nella creazione degli ordini.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Luogo di Destinazione</Label>
                  <Input value={editCompanyForm.ddt_destinazione_nome || ''} onChange={e => setECF('ddt_destinazione_nome', e.target.value)} placeholder="es. Magazzino Nord" data-testid="edit-ddt-destinazione" />
                </div>
                <div className="space-y-1.5">
                  <Label>Indirizzo</Label>
                  <Input value={editCompanyForm.ddt_destinazione_indirizzo || ''} onChange={e => setECF('ddt_destinazione_indirizzo', e.target.value)} placeholder="Via..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>CAP / Città</Label>
                  <Input value={editCompanyForm.ddt_destinazione_cap_citta || ''} onChange={e => setECF('ddt_destinazione_cap_citta', e.target.value)} placeholder="00100 Roma" />
                </div>
                <div className="space-y-1.5">
                  <Label>Causale Trasporto</Label>
                  <Input value={editCompanyForm.ddt_causale_trasporto || ''} onChange={e => setECF('ddt_causale_trasporto', e.target.value)} placeholder="es. Vendita" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Aspetto Esteriore Beni</Label>
                  <Input value={editCompanyForm.ddt_aspetto_beni || ''} onChange={e => setECF('ddt_aspetto_beni', e.target.value)} placeholder="es. Colli" />
                </div>
                <div className="space-y-1.5">
                  <Label>Trasporto a cura di</Label>
                  <Input value={editCompanyForm.ddt_trasporto_a_cura_di || ''} onChange={e => setECF('ddt_trasporto_a_cura_di', e.target.value)} placeholder="es. Mittente" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Vettore</Label>
                <Input value={editCompanyForm.ddt_vettore || ''} onChange={e => setECF('ddt_vettore', e.target.value)} placeholder="es. BRT, GLS..." />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditCompany(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={savingCompany} data-testid="save-edit-company-btn">
                {savingCompany ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save size={14} className="mr-2" />}Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Interaction Dialog */}
      <Dialog open={showInteraction} onOpenChange={setShowInteraction}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuova Interazione</DialogTitle></DialogHeader>
          <form onSubmit={addInteraction} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={intForm.type} onValueChange={v => setIntForm(f => ({...f, type:v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{INTERACTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Oggetto *</Label>
              <Input value={intForm.subject} onChange={e => setIntForm(f => ({...f, subject:e.target.value}))} required />
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={intForm.content} onChange={e => setIntForm(f => ({...f, content:e.target.value}))} rows={3} />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowInteraction(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
